class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
    this.isManuallyControlled = false;
    this.setupReliableHover();
  }
  setupReliableHover() {
    const details = this.mainDetailsToggle;
    const triggerCandidate = details.querySelector(
      ":scope > summary, :scope > a.header__menu-item"
    );
    const submenu = this.querySelector(".header__submenu");

    if (!details || !triggerCandidate || !submenu) return;

    const isSummaryTrigger = triggerCandidate.tagName === "SUMMARY";

    // Remove any existing event listeners that might conflict
    const triggerClone = triggerCandidate.cloneNode(true);
    triggerCandidate.parentNode.replaceChild(triggerClone, triggerCandidate);
    const triggerEl = triggerClone;

    // Ultra-robust state management
    let isMainHovering = false;
    let isSubmenuHovering = false;
    let hoverTimer = null;
    let forceCloseTimer = null;

    // Force correct initial state
    details.removeAttribute("open");
    triggerEl.setAttribute("aria-expanded", "false");

    // Apply force styles immediately
    submenu.style.cssText =
      "pointer-events: auto !important; z-index: 1000 !important;";

    const isMenuOpen = () => details.hasAttribute("open");

    const forceOpen = () => {
      clearTimeout(hoverTimer);
      clearTimeout(forceCloseTimer);

      this.isManuallyControlled = true;
      details.setAttribute("open", "");
      triggerEl.setAttribute("aria-expanded", "true");

      // Force submenu visibility with multiple approaches
      submenu.style.cssText =
        "pointer-events: auto !important; opacity: 1 !important; visibility: visible !important; z-index: 1000 !important; transform: translateY(0) !important;";

      // Ensure all submenu links are clickable
      const links = submenu.querySelectorAll("a, button");
      links.forEach((link) => {
        link.style.pointerEvents = "auto";
      });
    };

    const scheduleClose = () => {
      clearTimeout(hoverTimer);
      clearTimeout(forceCloseTimer);

      hoverTimer = setTimeout(() => {
        if (!isMainHovering && !isSubmenuHovering) {
          details.removeAttribute("open");
          triggerEl.setAttribute("aria-expanded", "false");
          this.isManuallyControlled = false;

          // Reset submenu styles
          submenu.style.cssText = "pointer-events: none !important;";
        }
      }, 150);
    };

    // Main container hover
    this.addEventListener("mouseenter", (e) => {
      isMainHovering = true;
      forceOpen();
    });

    this.addEventListener("mouseleave", (e) => {
      isMainHovering = false;

      // Check if we're moving to the submenu
      const relatedTarget = e.relatedTarget;
      if (
        relatedTarget &&
        (submenu.contains(relatedTarget) || relatedTarget === submenu)
      ) {
        return; // Don't close if moving to submenu
      }

      scheduleClose();
    });

    // Submenu-specific hover tracking
    submenu.addEventListener("mouseenter", (e) => {
      isSubmenuHovering = true;
      forceOpen();
    });

    submenu.addEventListener("mouseleave", (e) => {
      isSubmenuHovering = false;

      // Check if we're moving back to main menu
      const relatedTarget = e.relatedTarget;
      if (relatedTarget && this.contains(relatedTarget)) {
        return; // Don't close if moving within menu system
      }

      scheduleClose();
    });

    // Prevent default toggle behavior
    details.addEventListener("toggle", (e) => {
      if (this.isManuallyControlled) {
        e.stopPropagation();
      }
    });

    // Apply click/tap handling after helper functions exist
    triggerEl.addEventListener(
      "click",
      (e) => {
        if (!isSummaryTrigger && (e.metaKey || e.ctrlKey || e.shiftKey)) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        if (!details.hasAttribute("open")) {
          forceOpen();
        } else if (!isSummaryTrigger) {
          const href = triggerEl.getAttribute("href");
          if (href) {
            window.location.assign(href);
          }
        }

        return false;
      },
      true
    );

    // Safety mechanism - periodic check to ensure correct state
    const safetyCheck = () => {
      if (isMenuOpen() && !isMainHovering && !isSubmenuHovering) {
        // Check if mouse is actually over any part of the menu
        const menuRect = this.getBoundingClientRect();
        const submenuRect = submenu.getBoundingClientRect();

        if (!this.matches(":hover") && !submenu.matches(":hover")) {
          details.removeAttribute("open");
          triggerEl.setAttribute("aria-expanded", "false");
          this.isManuallyControlled = false;
        }
      }
    };

    setInterval(safetyCheck, 200);

    // Force immediate proper setup
    setTimeout(() => {
      if (!isMenuOpen()) {
        submenu.style.cssText = "pointer-events: none !important;";
      }
    }, 100);
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop"
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define("header-menu", HeaderMenu);
